import { api } from '@/lib/api';
import type {
  AIAgentQuery,
  AIAgentResponse,
  AIAgentWebhook,
  CreateAIAgentRequest,
  CreateAIAgentWebhookRequest,
  UpdateAIAgentRequest,
  UpdateAIAgentWebhookRequest,
  ListResponse,
  AgentVersionResponse
} from '@troia-v3/types';

/**
 * AI Agents API Service
 *
 * Serviço para gerenciar agentes de IA através da API REST
 */
export const agentsApi = {
  /**
   * Listar todos os agentes com paginação e filtros
   */
  async getAll(query?: AIAgentQuery): Promise<ListResponse<AIAgentResponse>> {
    const response = await api.get('/ai-agents', { params: query });
    return response.data.data;
  },

  /**
   * Buscar agente por ID
   */
  async getById(id: string): Promise<AIAgentResponse> {
    const response = await api.get(`/ai-agents/${id}`);
    return response.data.data;
  },

  /**
   * Criar novo agente
   */
  async create(data: CreateAIAgentRequest): Promise<AIAgentResponse> {
    const response = await api.post('/ai-agents', data);
    return response.data.data;
  },

  /**
   * Duplicar agente (cópia inativa "{nome} (cópia)"). POST sem body.
   */
  async duplicate(id: string): Promise<AIAgentResponse> {
    const response = await api.post(`/ai-agents/${id}/duplicate`);
    return response.data.data;
  },

  /**
   * Atualizar agente existente.
   *
   * @param overrideReason Se fornecido (mín 5 chars), envia header
   *   `X-Override-Reason` que faz o backend `DeployGate` ignorar:
   *   (a) ausência de snapshot pré-deploy nas últimas 24h, e/ou
   *   (b) regressão detectada acima do threshold de -3%.
   *   Usado quando o admin opta por pular o teste pré-deploy OU aceita
   *   conscientemente uma regressão detectada no teste.
   */
  async update(
    id: string,
    data: UpdateAIAgentRequest,
    overrideReason?: string,
  ): Promise<AIAgentResponse> {
    const headers: Record<string, string> =
      overrideReason && overrideReason.trim().length >= 5
        ? { 'X-Override-Reason': overrideReason.trim() }
        : {};
    const response = await api.patch(`/ai-agents/${id}`, data, { headers });
    return response.data.data;
  },

  /**
   * Deletar agente (soft delete)
   */
  async delete(id: string): Promise<void> {
    await api.delete(`/ai-agents/${id}`);
  },

  /**
   * Atualizar status do agente (ativar/desativar)
   */
  async updateStatus(id: string, status: 'active' | 'inactive'): Promise<AIAgentResponse> {
    const response = await api.patch(`/ai-agents/${id}/status`, { status });
    return response.data.data;
  },

  /**
   * Buscar histórico de versões do agente
   */
  async getVersions(agentId: string, query?: { page?: number; limit?: number }): Promise<{ items: AgentVersionResponse[]; total: number }> {
    const response = await api.get(`/ai-agents/${agentId}/versions`, { params: query });
    return response.data.data;
  },

  /**
   * Restaura uma versão antiga do agente — cria uma nova versão com o
   * `systemPrompt` da versão antiga (Sprint UI-4). O histórico é preservado.
   */
  async restoreVersion(agentId: string, versionId: string): Promise<AIAgentResponse> {
    const response = await api.post(
      `/ai-agents/${agentId}/versions/${versionId}/restore`,
    );
    return response.data.data;
  },

  /**
   * Criar um webhook individual do agente (salvamento imediato)
   */
  async createWebhook(agentId: string, data: CreateAIAgentWebhookRequest): Promise<AIAgentWebhook> {
    const response = await api.post(`/ai-agents/${agentId}/webhooks`, data);
    return response.data.data;
  },

  /**
   * Atualizar um webhook individual por id (name/description/method/prompt)
   */
  async updateWebhook(agentId: string, webhookId: string, data: UpdateAIAgentWebhookRequest): Promise<AIAgentWebhook> {
    const response = await api.patch(`/ai-agents/${agentId}/webhooks/${webhookId}`, data);
    return response.data.data;
  },

  /**
   * Ativar/desativar um webhook individual por id
   */
  async updateWebhookStatus(agentId: string, webhookId: string, status: 'active' | 'inactive'): Promise<AIAgentWebhook> {
    const response = await api.patch(`/ai-agents/${agentId}/webhooks/${webhookId}/status`, { status });
    return response.data.data;
  },

  /**
   * Excluir um webhook individual por id
   */
  async deleteWebhook(agentId: string, webhookId: string): Promise<void> {
    await api.delete(`/ai-agents/${agentId}/webhooks/${webhookId}`);
  },
};

// Re-export types for convenience
export type {
  AIAgentQuery,
  AIAgentResponse,
  AIAgentWebhook,
  CreateAIAgentRequest,
  CreateAIAgentWebhookRequest,
  UpdateAIAgentRequest,
  UpdateAIAgentWebhookRequest
};
